import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import NavbarT from '../../components/navbarTechnican';
import Footer from '../../homeComponents/footer';
import { useSelector } from 'react-redux';

const TechnicianDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({
    monthlyAssigned: 0,
    monthlyCompleted: 0,
    totalCompleted: 0,
    pending: 0
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const API_URL = process.env.REACT_APP_API_URL;
  const technicianId = useSelector((state) => state.auth.user.id);

  // Fetch service details for this technician
  const fetchServiceDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/services/getAll`);
      const allServices = response.data;
      
      // Filter services assigned to this technician
      const technicianServices = allServices.filter(service => {
        if (!service.technicianId) return false;
        
        // Handle both string and object formats
        if (typeof service.technicianId === 'string') {
          return service.technicianId === technicianId;
        }
        
        if (typeof service.technicianId === 'object') {
          return service.technicianId._id === technicianId;
        }
        
        return false;
      });
      
      setServices(technicianServices);
      calculateStats(technicianServices, selectedMonth, selectedYear);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics based on selected month/year
  const calculateStats = (services, month, year) => {
    // Filter services for the selected month/year
    const monthlyServices = services.filter(service => {
      if (!service.createdAt) return false;
      const serviceDate = new Date(service.createdAt);
      return serviceDate.getMonth() === month && 
             serviceDate.getFullYear() === year;
    });
    
    const monthlyAssigned = monthlyServices.length;
    
    const monthlyCompleted = monthlyServices.filter(service => 
      service.status === 'Completed'
    ).length;
    
    const totalCompleted = services.filter(service => 
      service.status === 'Completed'
    ).length;
    
    const pending = services.filter(service => 
      service.status === 'Pending'
    ).length;
    
    setStats({
      monthlyAssigned,
      monthlyCompleted,
      totalCompleted,
      pending
    });
  };

  useEffect(() => {
    if (technicianId) {
      fetchServiceDetails();
    }
  }, [technicianId]);

  // Recalculate stats when month/year changes
  useEffect(() => {
    if (services.length > 0) {
      calculateStats(services, selectedMonth, selectedYear);
    }
  }, [selectedMonth, selectedYear, services]);

  // Filter services based on selected month/year
  const filteredServices = services.filter(service => {
    if (!service.createdAt) return false;
    const serviceDate = new Date(service.createdAt);
    return serviceDate.getMonth() === selectedMonth && 
           serviceDate.getFullYear() === selectedYear;
  });

  // Get month name from index
  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  // Generate year options
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 1, currentYear, currentYear + 1];
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (!technicianId) {
    return (
      <div className='font-clash bg-banner min-h-screen'>
        <NavbarT />
        <div className="pt-44 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-white">Please log in to view your dashboard</h1>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className='font-clash bg-banner min-h-screen'>
      <Helmet>
        <title>Technician Dashboard</title>
        <meta
          name="keywords"
          content="Pdi, Bengaluru, Bangalore, Kardo, Inspection, Ze, Pre delivery, Services, Car, Cars, Vaidya, BHP, Team, CID, Expert, In, Best, 24, BOSS, Dail, Just, New car, Checklist, Detailed, Report, Inspect, Reliable, Blog, Used car inspection, Buying a new car"
        />
        <meta
          name="description"
          content="Explore our reliable pre-delivery inspection services for new and used cars. Detailed reports, expert insights, and the best service in Bangalore and beyond."
        />
      </Helmet>
      
      <NavbarT />
      
      <div className="pt-44 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-white">Your Technician Dashboard</h1>
          <p className="mt-2 text-lg text-gray-300">
            Track your service assignments and completion rates
          </p>
        </div>
        
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Your Performance Overview</h2>
            <p className="text-gray-500">Filter by month and year</p>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1">
                Month
              </label>
              <select
                id="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i}>
                    {getMonthName(i)}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                {getYearOptions().map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Stats Cards */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {/* Monthly Assigned */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md p-6 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Monthly Assigned</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.monthlyAssigned}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Services assigned to you in {getMonthName(selectedMonth)} {selectedYear}
              </p>
            </div>
            
            {/* Monthly Completed */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Monthly Completed</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.monthlyCompleted}</p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Services you completed in {getMonthName(selectedMonth)} {selectedYear}
              </p>
            </div>
            
            {/* Total Completed */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-md p-6 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Total Completed</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalCompleted}</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                All services you've completed
              </p>
            </div>
            
            {/* Pending Services */}
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl shadow-md p-6 border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-600">Pending Services</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.pending}</p>
                </div>
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Your services awaiting completion
              </p>
            </div>
          </div>
        )}
        
        {/* Completion Rate Chart */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-10">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Your Completion Rate</h2>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {stats.monthlyAssigned > 0 
                ? `${Math.round((stats.monthlyCompleted / stats.monthlyAssigned) * 100)}%`
                : '0%'}
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div 
              className="bg-blue-600 h-4 rounded-full" 
              style={{ 
                width: stats.monthlyAssigned > 0 
                  ? `${Math.round((stats.monthlyCompleted / stats.monthlyAssigned) * 100)}%` 
                  : '0%' 
              }}
            ></div>
          </div>
          
          <div className="flex justify-between text-sm text-gray-600">
            <span>Assigned to you: {stats.monthlyAssigned}</span>
            <span>Completed by you: {stats.monthlyCompleted}</span>
          </div>
        </div>
        
        {/* Recent Services */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              Your Services for {getMonthName(selectedMonth)} {selectedYear}
            </h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No services found</h3>
              <p className="mt-1 text-gray-500">No services assigned to you for this period.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inspection Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredServices.map((service) => (
                    <tr key={service._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="bg-gray-200 border-2 border-dashed rounded-xl w-10 h-10" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{service.customerName}</div>
                            <div className="text-sm text-gray-500">{service.customerPhone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{service.vehicleMake} {service.vehicleModel}</div>
                        <div className="text-sm text-gray-500">{service.variant}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {service.inspectionType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(service.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          service.status === 'Completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {service.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TechnicianDashboardPage;